import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { faFlag } from "@fortawesome/free-regular-svg-icons";
import { faRotate } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextareaAutosize } from "@mui/base";
import mixpanel from "mixpanel-browser";
import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Controller, useForm } from "react-hook-form";

import FLAG_NOTE_TO_SUPPORT from "../../../../graphql/mutations/FlagNoteToSupport";
import { RETRY_FAILED_AUTO_SOAP } from "../../../../graphql/mutations/RetryFailedAutoSoap";
import {
  usePremiumFeature,
  UserPermissions,
} from "../../../../hooks/use-premium-feature";
import { alert } from "../../../common/Alert";
import { BasicButton } from "../../../common/buttons/BasicButton";
import { MessageModal } from "../../../common/modals/PopupModals";

const GET_USER = gql`
  {
    user {
      uuid
      email
      firstName
      lastName
    }
  }
`;
export default function SomethingWrongButton({ note, refetchNote }) {
  const [shouldShowFlagNoteModal, setShouldShowFlagNoteModal] =
    useState(false);
  const { control, getValues, reset } = useForm({
    defaultValues: {
      flagComment: "",
    },
  });
  const { hasFeatureAccess } = usePremiumFeature();

  const [retryFailedAutoSoap] = useMutation(RETRY_FAILED_AUTO_SOAP);
  const [flagNoteToSupport] = useMutation(FLAG_NOTE_TO_SUPPORT);

  const [getUser] = useLazyQuery(GET_USER, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  let submitting = useRef(false);

  function handleSubmit() {
    if (submitting.current) {
      return false;
    }
    submitting.current = true;
    const data = getValues();
    getUser().then((response) => {
      flagNoteToSupport({
        variables: {
          email: response?.data?.user?.email,
          customerName: `${response?.data?.user?.firstName} ${response?.data?.user?.lastName}`,
          comment: data?.flagComment,
          noteUuid: note?.uuid,
          title: note?.title,
        },
      })
        .then(() => {
          alert(
            "success",
            hasFeatureAccess(UserPermissions.HUMAN_SUPPORT_ENABLED)
              ? "Sent info to our support team. We'll reach out when it's fixed!"
              : "Thanks! Your feedback has been recorded.",
          );
          reset();
          setShouldShowFlagNoteModal(false);
        })
        .catch((err) => console.log(err))
        .finally(() => {
          submitting.current = false;
        });
    });
  }
  const handleRetryFailedAutoSoap = async () => {
    try {
      await retryFailedAutoSoap({
        variables: {
          noteUuid: note?.uuid,
          jobType: note?.jobType,
          templateUuid: note?.autoTemplates?.uuid,
        },
      });
      refetchNote();
      alert("success", "Reprocessing this note!");
      setShouldShowFlagNoteModal(false);
      mixpanel.track("Note reprocessed", {
        noteUuid: note?.uuid,
        jobType: note?.jobType,
        templateUuid: note?.templateUuid,
        source: "Something Wrong Button",
      });
    } catch (e) {
      mixpanel.track("Failed to reprocess note", {
        noteUuid: note?.uuid,
        jobType: note?.jobType,
        templateUuid: note?.templateUuid,
        error: e.message,
        source: "Something Wrong Button",
      });
      if (e.message === "note.autoTemplates is null") {
        alert(
          "error",
          "The template for this note has been deleted. Please use the 'Use Template' button to apply a new template.",
        );
      } else {
        alert(
          "error",
          "Failed to reprocess the note. Please try again later.",
        );
      }
    }
  };

  return (
    <>
      {createPortal(
        <MessageModal
          shouldShow={shouldShowFlagNoteModal}
          setShouldShow={(state) => {
            setShouldShowFlagNoteModal(state);
            reset();
          }}
          dismissible
          title="Something Wrong?"
          width="max-w-2xl"
        >
          <div className="flex flex-row items-start divide-x dark:divide-gray-600/50 p-2">
            <div className="flex-1 pr-6 space-y-2">
              <h4>Retry Processing</h4>
              <div className="flex flex-row items-center space-x-2 border border-gray-300 dark:border-gray-700 rounded-full justify-between">
                <h4 className="text-sm md:text-base mx-4 truncate">
                  {note?.jobTypeName || note?.jobType}
                </h4>
                <BasicButton
                  onClick={handleRetryFailedAutoSoap}
                  data-testid="retry-processing-button"
                >
                  Retry{" "}
                  <FontAwesomeIcon className="ml-2" icon={faRotate} />
                </BasicButton>
              </div>

              <h5 className="text-xs md:text-sm text-gray-500">
                If you'd like to apply a different template, please
                close this menu and click the{" "}
                <strong>Use Template</strong> button instead.
              </h5>
            </div>
            <div className="flex flex-col flex-1 pl-6 space-y-2">
              <h4>Send to Support</h4>
              <h5 className="text-xs md:text-sm text-gray-500">
                {hasFeatureAccess(
                  UserPermissions.HUMAN_SUPPORT_ENABLED,
                )
                  ? "Let us know what's wrong, and we'll look into it. More details are preferred!"
                  : "Something wrong? Your feedback helps us improve Scribenote's AI for everyone. More details are preferred!"}
              </h5>
              <Controller
                render={({ field }) => (
                  <TextareaAutosize
                    minRows={5}
                    placeholder={"The note is blank!"}
                    className="resize-none appearance-none relative block w-full px-2 py-1 border dark:bg-gray-700 dark:border-gray-700 dark:text-gray-200 border-gray-100 bg-gray-100 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:z-10 text-xs md:text-sm h-8"
                    {...field}
                  />
                )}
                control={control}
                name="flagComment"
              />
              <div className="self-end">
                <BasicButton onClick={() => handleSubmit()}>
                  Send →
                </BasicButton>
              </div>
            </div>
          </div>
        </MessageModal>,
        document.getElementById("root-node"),
      )}

      <button
        className={`text-gray-400 dark:text-gray-500 dark:hover:text-gray-700 hover:bg-red-400 hover:text-white px-2 py-1 rounded-md text-xs md:text-sm transition-all`}
        onClick={() =>
          setShouldShowFlagNoteModal(!shouldShowFlagNoteModal)
        }
        data-testid="something-wrong-button"
      >
        <FontAwesomeIcon icon={faFlag} /> Something Wrong?
      </button>
    </>
  );
}
